import { useAuth0 } from '@auth0/auth0-react';
import { XMarkIcon } from '@heroicons/react/20/solid';
import {
  type Control,
  Controller,
  type FieldErrors,
  type UseFormRegister,
  useFieldArray,
} from 'react-hook-form';
import { SmallSecondaryButton } from '../../../components/buttons';
import { Field, Input, Select } from '../../../components/forms';
import { Loading } from '../../../components/loading';
import { StatusCell } from '../../../components/status/status-cell';
import { Cell, Row, Table } from '../../../components/tables';
import { type Store, UserRole, UserRoleLabel } from '../../../data/store';
import type { FormValues } from './form-data';

type UsersFormProps = {
  control: Control<FormValues>;
  errors: FieldErrors<FormValues>;
  register: UseFormRegister<FormValues>;
  store: Store;
};

function sameEmail(a: string, b: string | undefined) {
  return b !== undefined && a.toLowerCase() === b.toLowerCase();
}

// Demo is assigned by the system, never picked by an owner.
const roleOptions = Object.values(UserRole)
  .filter((role) => role !== UserRole.Demo)
  .map((role) => ({ value: role, label: UserRoleLabel[role] }));

export function UsersForm({ control, errors, register, store }: UsersFormProps) {
  const { user } = useAuth0();
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'users',
  });

  if (!user) {
    return <Loading />;
  }

  return (
    <div className="flex flex-col gap-4">
      <Table headings={['Email', 'Role', '']}>
        {fields.map((field, index) => {
          // Auth0 hands back the address as typed at sign-up; the API stores
          // it lowercased. Same person either way.
          const isCurrentUser = sameEmail(field.email, user.email);
          const isExistingUser = store.users.some(
            (user) => sameEmail(user.email, field.email) && user.store_id === field.store_id
          );
          // You can't remove yourself here, and for the same reason you can't
          // demote yourself: either one locks you out of the page you're on.
          const showDeleteButton = !isCurrentUser && (store.users.length > 1 || !isExistingUser);

          return (
            <Row key={field.id}>
              <Cell>
                {isExistingUser && (
                  <span className="flex items-center gap-2 h-8 text-gray-500">
                    {field.email}
                    {isCurrentUser && (
                      <StatusCell variant="muted" tone="outline" weight="medium">
                        You
                      </StatusCell>
                    )}
                  </span>
                )}
                {!isExistingUser && (
                  <Field
                    label=""
                    name={`users.${index}.email`}
                    errorMessage={errors.users?.[index]?.email?.message}
                  >
                    <Input
                      {...register(`users.${index}.email`)}
                      invalid={!!errors.users?.[index]?.email?.message}
                      placeholder="jane@example.com"
                      type="email"
                    />
                  </Field>
                )}
              </Cell>
              <Cell>
                {isCurrentUser && (
                  <span className="flex items-center h-8 text-gray-500">
                    {UserRoleLabel[field.relation] ?? field.relation}
                  </span>
                )}
                {!isCurrentUser && (
                  <Field
                    label=""
                    name={`users.${index}.relation`}
                    errorMessage={errors.users?.[index]?.relation?.message}
                  >
                    <Controller
                      control={control}
                      name={`users.${index}.relation`}
                      render={({ field: relationField }) => (
                        <Select<UserRole>
                          value={relationField.value}
                          onChange={relationField.onChange}
                          options={roleOptions}
                          // react-select sizes to the selected label, so without
                          // this the column jumps between "Owner" and "Employee".
                          className="w-40"
                        />
                      )}
                    />
                  </Field>
                )}
              </Cell>
              <Cell>
                {showDeleteButton && (
                  <button
                    type="button"
                    className="flex h-9 w-9 items-center justify-center rounded-md text-icon hover:bg-gray-100 cursor-pointer focus:outline-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-primary-600"
                    onClick={() => remove(index)}
                    title="Remove user"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                )}
              </Cell>
            </Row>
          );
        })}
      </Table>
      <SmallSecondaryButton
        type="button"
        onClick={() => append({ store_id: store.id, email: '', relation: UserRole.Employee })}
        disabled={fields.length >= 10}
      >
        Add User {fields.length >= 10 && '(Maximum 10 users)'}
      </SmallSecondaryButton>
    </div>
  );
}
